import {
  Box,
  Modal,
  Typography,
  TableCell,
  TableRow,
  TablePagination,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import HeaderDrawer from '../../Drawers/HeaderDrawer';
import SearchInputModal from '../User/ProjectsSites/SearchInputModal';
import { UserResponse } from '../../../models/User';
import { FlexBox } from '../../tableItems';
import { DefaultCheckbox } from '../../inputs';
import { onlyNumbers } from '../../../helpers/validateInputs';
import { getAllUsers } from '../../../helpers/user';
import Spinner from '../../Loading/Spinner';
import UsersSiteTable from './UsersSiteTable';
import { UsersSites } from '../../../models/Forms';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 850,
  bgcolor: 'background.paper',
  borderRadius: '2px',
  boxShadow: 24,
  overflowY: 'auto',
  pb: 5,
};

interface UsersSelectionProps {
  open: boolean;
  closeModal: React.Dispatch<React.SetStateAction<boolean>>;
  updateUsers: (usersArray: UsersSites[]) => void;
  selectedSitesForm: UsersSites[];
  usersDefaultArray: UserResponse[];
  usersDefaultCount: number;
  usersDefaultLoading: boolean;
}

const UsersSelection = ({
  open,
  closeModal,
  updateUsers,
  selectedSitesForm,
  usersDefaultArray,
  usersDefaultCount,
  usersDefaultLoading,
}: UsersSelectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [usersArr, setUsersArr] = useState<UserResponse[]>([]);
  const [selected, setSelected] = useState<UsersSites[]>(selectedSitesForm);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(0);

  const fetchUsers = async (
    pageNumber?: number,
    pageSizePagination?: number,
    searchValue?: string,
  ) => {
    setIsLoading(true);
    try {
      const users = await getAllUsers(
        pageNumber ?? page + 1,
        pageSizePagination ?? pageSize,
        searchValue ?? search,
        'username',
      );
      setUsersArr(users.results);
      setCount(users.count);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setSelected(selectedSitesForm);
  }, [selectedSitesForm]);

  useEffect(() => {
    setUsersArr(usersDefaultArray);
  }, [usersDefaultArray]);

  useEffect(() => {
    setCount(usersDefaultCount);
  }, [usersDefaultCount]);

  useEffect(() => {
    setIsLoading(usersDefaultLoading);
  }, [usersDefaultLoading]);

  useEffect(() => {
    if (!open && search !== '') {
      setSearch('');
      setPage(0);
      fetchUsers(1, pageSize, '');
    }
  }, [open]);

  const handleChangeOfValue = (
    id: number,
    value: string,
    type: 'minimum' | 'percentage',
  ) => {
    const copySelected = [...selected];
    const indexSelected = copySelected.findIndex(user => user.id === id);
    if (indexSelected !== -1) {
      if (type === 'minimum') copySelected[indexSelected].minimum_links = value;
      if (type === 'percentage')
        copySelected[indexSelected].percentage_allowed = value;
      setSelected(copySelected);
    }
  };

  const handleChecked = (
    id: number,
    minimum_links: string,
    username: string,
    percentage_allowed: string,
  ) => {
    const copySelected = [...selected];
    const index = copySelected.findIndex(user => user.id === id);
    if (index !== -1) {
      copySelected.splice(index, 1);
    } else {
      copySelected.push({
        id,
        username,
        minimum_links,
        percentage_allowed,
      });
    }
    setSelected(copySelected);
  };

  const isSelected = (id: number) =>
    selected.findIndex(user => user.id === id) !== -1;

  const userMinimum = (id: number) => {
    const userNumber = selected.find(user => user.id === id);
    return userNumber ? userNumber.minimum_links : '50';
  };

  const userPercentage = (id: number) => {
    const userNumber = selected.find(user => user.id === id);
    return userNumber ? userNumber.percentage_allowed : '0';
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
    fetchUsers(1, parseInt(event.target.value, 10));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchUsers(newPage + 1);
  };

  const handleSearch = (value: string) => {
    setSearch(value);
    setPage(0);
    fetchUsers(1, pageSize, value);
  };

  return (
    <Modal open={open}>
      <Box sx={style} className="site_access-responsive">
        <HeaderDrawer
          title="User Site Access"
          threeButtons={false}
          saveFunction={() => {
            updateUsers(selected);
            closeModal(false);
          }}
          closeDrawer={closeModal}
          loading={false}
          disabled={false}
        />
        <Box paddingX={2.5} paddingY={2.4}>
          <Typography fontSize={12} marginBottom={1.3}>
            To selecte a user, search below
          </Typography>
          <Box position="relative">
            <SearchInputModal
              onChangeFunc={(value: string) => handleSearch(value)}
              onBlurFunc={() => {}}
              onFocusFunc={() => {}}
            />
          </Box>
          <Box marginTop={3}>
            <UsersSiteTable
              columnsNames={{
                first: 'User',
                second: 'Minimum Links for Cannibalization',
                third: 'Percentage Allowed',
              }}
            >
              {isLoading && <Spinner />}
              {!isLoading && usersArr.length === 0 && (
                <Typography textAlign="center" marginTop={2} fontWeight={600}>
                  No results found
                </Typography>
              )}
              {!isLoading && usersArr.length > 0 && (
                <>
                  {usersArr.map(user => {
                    return (
                      <TableRow
                        key={user.id}
                        hover
                        role="checkbox"
                        selected={isSelected(user.id)}
                      >
                        <TableCell component="th" scope="row">
                          <FlexBox>
                            <DefaultCheckbox
                              size="small"
                              sx={{ p: 0, marginRight: 1 }}
                              disabled={false}
                              onClick={() =>
                                handleChecked(
                                  user.id,
                                  userMinimum(user.id),
                                  user.username,
                                  userPercentage(user.id),
                                )
                              }
                              checked={isSelected(user.id)}
                            />
                            <Typography fontSize={14}>
                              {user.username}
                            </Typography>
                          </FlexBox>
                        </TableCell>
                        <TableCell align="center">
                          <input
                            placeholder="0"
                            value={userMinimum(user.id)}
                            type="number"
                            disabled={!isSelected(user.id)}
                            onChange={e =>
                              handleChangeOfValue(
                                user.id,
                                onlyNumbers(e.target.value),
                                'minimum',
                              )
                            }
                            className="min-links-field"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <input
                            placeholder="0"
                            value={userPercentage(user.id)}
                            type="number"
                            disabled={!isSelected(user.id)}
                            onChange={e =>
                              handleChangeOfValue(
                                user.id,
                                onlyNumbers(e.target.value),
                                'percentage',
                              )
                            }
                            className="min-links-field"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </>
              )}
            </UsersSiteTable>
            {!isLoading && usersArr.length > 0 && (
              <TablePagination
                component="div"
                count={count}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default UsersSelection;
